import React, { useState, useEffect } from "react";
import { Button, TextInput, Textarea, Modal } from "@mantine/core";
import apiService from "../../services/api";
import type { ClientPortalProps } from "../../types";

interface TransformedTrainer {
  id: string;
  name: string;
  title: string;
  experience: string;
  photo: string;
  specializations: string[];
  philosophy: string[];
  credentials: string[];
  baseRate: number;
  perMinuteRate: string;
  overallRating: number;
  totalReviews: number;
  isOnline: boolean;
  bio: string;
  reviews: unknown[];
  [key: string]: unknown;
}

interface TrainerFilters {
  search: string;
  philosophy: string[];
  expertise: string[];
  credentials: string[];
}

interface DropdownState {
  philosophy: boolean;
  expertise: boolean;
  credentials: boolean;
}

interface SessionHistoryItem {
  id: string;
  session_request_id?: string;
  trainer_id?: string;
  trainer_name?: string;
  date?: string;
  end_date?: string;
  duration_minutes?: number;
  session_duration_minutes?: number;
  price?: number;
  status?: string;
  summary?: string;
  training_tasks?: unknown;
  user_rating?: number | null;
  [key: string]: unknown;
}

interface TrainingRequest {
  id: string;
  trainer_name?: string;
  status: string;
  first_10_min_rate: number;
  per_minute_rate: number;
  total_price: number | null;
  duration_minutes: number | null;
  created_at: string | null;
  accepted_at: string | null;
  payment_status: string;
  client_joined_at: string | null;
  trainer_joined_at: string | null;
  notes?: string;
  [key: string]: unknown;
}

interface ClientProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  address?: string;
  profile_photo_url?: string;
  favorite_trainer?: { name: string } | null;
  [key: string]: unknown;
}

interface LocalPet {
  id: string;
  pet_name: string;
  age: number | string;
  race: string;
  target_behaviors?: string;
  photo_url?: string;
  name?: string;
  [key: string]: unknown;
}

interface ProfileEditData {
  first_name: string;
  last_name: string;
  phone_number: string;
  address: string;
  password: string;
  confirmPassword: string;
}

interface NewPetData {
  pet_name: string;
  age: string;
  race: string;
  target_behaviors: string;
}

interface DeliverableData {
  summary?: string;
  checklist_items?: string[];
  [key: string]: unknown;
}

interface NormalizedTask {
  description: string;
  completed: boolean;
  notes?: string;
}

interface PetCardProps {
  pet: LocalPet;
  onUpdate: (petId: string, data: Record<string, unknown>) => void;
  onDelete: (petId: string) => void;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case "completed":
      return "text-success";
    case "in_progress":
      return "text-primary";
    default:
      return "text-destructive";
  }
};

// Defined outside ClientPortal so internal edit state isn't lost when the parent re-renders.
const PetCard: React.FC<PetCardProps> = ({ pet, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    pet_name: pet.pet_name,
    age: pet.age,
    race: pet.race,
    target_behaviors: pet.target_behaviors || "",
  });
  const handleSave = (): void => {
    onUpdate(pet.id, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      pet_name: pet.pet_name,
      age: pet.age,
      race: pet.race,
      target_behaviors: pet.target_behaviors || "",
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-muted rounded-xl p-5 border border-border shadow-sm transition-all duration-200 relative min-h-[280px] flex flex-col">
      <div className="flex justify-end items-start mb-3">
        <div className="flex gap-1">
          {!isEditing ? (
            <>
              <Button
                size="compact-sm"
                color="blue"
                onClick={() => setIsEditing(true)}
                title="Edit"
              >
                Edit
              </Button>
              <Button
                size="compact-sm"
                color="red"
                onClick={() => onDelete(pet.id)}
                title="Delete"
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button size="compact-sm" color="green" onClick={handleSave} title="Save">
                Save
              </Button>
              <Button size="compact-sm" color="gray" onClick={handleCancel} title="Cancel">
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pet Photo or Icon */}
      <div className="text-center mb-3">
        {pet.photo_url ? (
          <img
            src={pet.photo_url}
            alt={pet.pet_name || "Pet"}
            className="w-[120px] h-[120px] rounded-full object-cover border-[3px] border-primary mx-auto"
          />
        ) : (
          <div className="text-5xl text-center mb-3">🐾</div>
        )}
      </div>

      {isEditing ? (
        <div className="flex flex-col gap-2.5">
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Pet Name
            </label>
            <TextInput
              value={editData.pet_name}
              onChange={(e) => setEditData({ ...editData, pet_name: e.target.value })}
              placeholder="Name"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Age (years)
            </label>
            <TextInput
              type="number"
              value={String(editData.age)}
              onChange={(e) => setEditData({ ...editData, age: e.target.value })}
              placeholder="Age"
              min={0}
              max={30}
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Breed
            </label>
            <TextInput
              value={editData.race}
              onChange={(e) => setEditData({ ...editData, race: e.target.value })}
              placeholder="Breed"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Target Behaviors
            </label>
            <TextInput
              value={editData.target_behaviors}
              onChange={(e) => setEditData({ ...editData, target_behaviors: e.target.value })}
              placeholder="Target Behaviors"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-2">
          <div className="text-xl font-bold text-foreground mb-2 text-center">{pet.pet_name}</div>
          <div className="flex flex-col gap-1">
            <div className="text-sm text-muted-foreground text-center">{pet.age} years old</div>
            <div className="text-sm text-muted-foreground text-center">{pet.race || "Mixed"}</div>
          </div>
          {pet.target_behaviors && (
            <div className="text-xs text-muted-foreground text-center mt-2 italic">
              {pet.target_behaviors}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClientPortal: React.FC<ClientPortalProps> = ({ onBackToHome, onLogout, isLoggedIn }) => {
  const [activeTab, setActiveTab] = useState<string>("find-trainer");
  const [pets, setPets] = useState<LocalPet[]>([]);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [activeTrainers, setActiveTrainers] = useState<TransformedTrainer[]>([]);
  const [filteredTrainers, setFilteredTrainers] = useState<TransformedTrainer[]>([]);
  const [trainerFilters, setTrainerFilters] = useState<TrainerFilters>({
    search: "",
    philosophy: [],
    expertise: [],
    credentials: [],
  });
  const [openDropdowns, setOpenDropdowns] = useState<DropdownState>({
    philosophy: false,
    expertise: false,
    credentials: false,
  });
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryItem[]>([]);
  const [sessionDeliverables, setSessionDeliverables] = useState<Record<string, DeliverableData>>(
    {},
  ); // Map session_request_id to deliverables
  const [activeTrainingRequests, setActiveTrainingRequests] = useState<TrainingRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const [selectedTrainer, setSelectedTrainer] = useState<TransformedTrainer | null>(null);
  const [showCancelPopup, setShowCancelPopup] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<TrainingRequest | null>(null);
  const [selectedPetId, setSelectedPetId] = useState<string>("");
  const [requestExplanation, setRequestExplanation] = useState<string>("");
  const [requestDuration, setRequestDuration] = useState<number>(30);
  const [isEditingProfile, setIsEditingProfile] = useState<boolean>(false);
  const [profileEditData, setProfileEditData] = useState<ProfileEditData>({
    first_name: "",
    last_name: "",
    phone_number: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [refreshSuccess, setRefreshSuccess] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<SessionHistoryItem | null>(null);
  const [showSessionModal, setShowSessionModal] = useState<boolean>(false);
  const [showAddPetModal, setShowAddPetModal] = useState<boolean>(false);
  const [newPetData, setNewPetData] = useState<NewPetData>({
    pet_name: "",
    age: "",
    race: "",
    target_behaviors: "",
  });
  const [showCouponModal, setShowCouponModal] = useState<boolean>(false);
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
  const [selectedRequestForPayment, setSelectedRequestForPayment] =
    useState<TrainingRequest | null>(null);
  const [couponCode, setCouponCode] = useState<string>("");

  const clientId = localStorage.getItem("clientId");

  const formatCurrency = (value: unknown): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "$0.00";
    }
    return `$${Number(value).toFixed(2)}`;
  };

  const _normalizeTasks = (rawTasks: unknown): NormalizedTask[] => {
    if (!rawTasks) return [];

    let parsed: unknown = rawTasks;
    if (typeof rawTasks === "string") {
      try {
        parsed = JSON.parse(rawTasks);
      } catch (err) {
        console.log("Failed to parse as JSON array:", err);
        parsed = [rawTasks];
      }
    }

    if (!Array.isArray(parsed)) {
      parsed = [parsed];
    }

    return (parsed as unknown[])
      .map((task: unknown) => {
        if (typeof task === "string") {
          return {
            description: task,
            completed: false,
          };
        }
        if (task && typeof task === "object") {
          const t = task as Record<string, unknown>;
          const description = (t.description || t.title || "") as string;
          if (!description) return null;
          return {
            description,
            completed: Boolean(t.completed),
            notes: (t.notes || "") as string,
          };
        }
        return null;
      })
      .filter((t): t is NormalizedTask => t !== null);
  };
  console.log("=== CLIENT PORTAL LOADED ===");
  console.log("Client ID from localStorage:", clientId);
  console.log("All localStorage:", localStorage);
  console.log("isLoggedIn prop:", isLoggedIn);

  const applyTrainerFilters = (): void => {
    let filtered = [...activeTrainers];

    if (trainerFilters.search) {
      filtered = filtered.filter(
        (trainer) =>
          trainer.name.toLowerCase().includes(trainerFilters.search.toLowerCase()) ||
          trainer.bio.toLowerCase().includes(trainerFilters.search.toLowerCase()),
      );
    }

    if (trainerFilters.philosophy && trainerFilters.philosophy.length > 0) {
      filtered = filtered.filter(
        (trainer) =>
          trainer.philosophy &&
          trainerFilters.philosophy.some((phil) => trainer.philosophy.includes(phil)),
      );
    }

    if (trainerFilters.expertise && trainerFilters.expertise.length > 0) {
      filtered = filtered.filter(
        (trainer) =>
          trainer.specializations &&
          trainerFilters.expertise.some((exp) => trainer.specializations.includes(exp)),
      );
    }

    if (trainerFilters.credentials && trainerFilters.credentials.length > 0) {
      filtered = filtered.filter(
        (trainer) =>
          trainer.credentials &&
          trainerFilters.credentials.some((cred) => trainer.credentials.includes(cred)),
      );
    }

    setFilteredTrainers(filtered);
  };

  const handleFilterToggle = (
    filterType: "philosophy" | "expertise" | "credentials",
    value: string,
  ): void => {
    setTrainerFilters((prev) => {
      const currentArray = prev[filterType] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value];
      return {
        ...prev,
        [filterType]: newArray,
      };
    });
  };

  const toggleDropdown = (dropdownName: "philosophy" | "expertise" | "credentials"): void => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownName]: !prev[dropdownName],
    }));
  };

  useEffect(() => {
    applyTrainerFilters();
  }, [activeTrainers, trainerFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!(event.target as HTMLElement).closest("[data-dropdown]")) {
        setOpenDropdowns({
          philosophy: false,
          expertise: false,
          credentials: false,
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (clientId) {
      loadInitialData();
    }
  }, [clientId]);

  const loadInitialData = async (): Promise<void> => {
    try {
      setLoading(true);

      // Load trainers by default since activeTab starts as 'find-trainer'
      if (activeTab === "find-trainer") {
        await loadActiveTrainers();
      } else if (activeTab === "pets") {
        await loadPets();
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadPets = async (): Promise<void> => {
    try {
      console.log("=== LOADING PETS ===");
      console.log("Client ID:", clientId);
      const response = (await apiService.getClientPets(clientId!)) as Record<string, unknown>;
      console.log("Pets API response:", response);
      // Backend returns "Data" (capital D), not "data"
      const petsData = (response.Data as LocalPet[]) || [];
      console.log("Pets data extracted:", petsData);
      setPets(petsData);
      console.log("Pets state set to:", petsData);
    } catch (err) {
      console.error("Error loading pets:", err);
      setError("Failed to load pets");
    }
  };

  const loadProfile = async (): Promise<void> => {
    try {
      const response = (await apiService.getClientProfile(clientId!)) as Record<string, unknown>;
      if (response.Code === 200) {
        setProfile(response.data as ClientProfile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("Failed to load profile");
    }
  };

  const handleClientPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB");
      return;
    }

    if (!clientId) {
      setError("Client ID not found");
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const response = (await apiService.uploadClientProfilePhoto(clientId!, file)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        // Reload profile to get updated photo URL
        await loadProfile();
        alert("Profile photo uploaded successfully!");
      } else {
        setError((response.Message as string) || "Failed to upload photo");
      }
    } catch (err) {
      console.error("Photo upload error:", err);
      setError((err as Error).message || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const loadActiveTrainers = async (): Promise<void> => {
    try {
      setRefreshSuccess(false);
      setLoading(true);
      console.log("=== LOADING ACTIVE TRAINERS ===");
      console.log("Current time:", new Date().toISOString());
      console.log("Loading active trainers...");

      // Force a fresh API call by adding a timestamp parameter
      const response = (await apiService.getActiveTrainers()) as Record<string, unknown>;
      const trainersArray = response.trainers as Record<string, unknown>[] | undefined;
      console.log("Active trainers response:", response);
      console.log("Response Code:", response.Code);
      console.log("Response trainers:", trainersArray);
      console.log(
        "Response trainers length:",
        trainersArray ? trainersArray.length : "no trainers",
      );

      if (response.Code === 200 && trainersArray && trainersArray.length > 0) {
        console.log("Transforming trainer data...");

        // Helper function to parse Python-style list strings
        const parseListString = (value: unknown): string[] => {
          if (!value) return [];
          if (Array.isArray(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return [];

            // Try JSON.parse first (handles JSON arrays with double quotes)
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) return parsed;
            } catch (err) {
              // Not valid JSON, continue
              console.log("Failed to parse as JSON array, trying manual parsing:", err);
            }

            // Try to parse Python-style list strings (e.g., "['item1', 'item2']")
            if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
              try {
                // Replace single quotes with double quotes for JSON parsing
                const jsonString = trimmed.replace(/'/g, '"');
                const parsed = JSON.parse(jsonString);
                if (Array.isArray(parsed)) return parsed;
              } catch (err) {
                console.log("Failed to parse as JSON array:", err);
                // If that fails, try to extract items manually
                const inner = trimmed.slice(1, -1).trim();
                if (inner) {
                  const items = inner
                    .split(",")
                    .map((item) => {
                      let cleaned = item.trim();
                      // Remove surrounding quotes if present
                      if (
                        (cleaned.startsWith("'") && cleaned.endsWith("'")) ||
                        (cleaned.startsWith('"') && cleaned.endsWith('"'))
                      ) {
                        cleaned = cleaned.slice(1, -1);
                      }
                      return cleaned.trim();
                    })
                    .filter(Boolean);
                  if (items.length > 0) return items;
                }
              }
            }

            // Fall back to comma-separated
            return trimmed
              .split(",")
              .map((item) => {
                let cleaned = item.trim();
                if (
                  (cleaned.startsWith("'") && cleaned.endsWith("'")) ||
                  (cleaned.startsWith('"') && cleaned.endsWith('"'))
                ) {
                  cleaned = cleaned.slice(1, -1);
                }
                return cleaned.trim();
              })
              .filter(Boolean);
          }
          return [];
        };

        // Helper to parse price
        const parsePrice = (value: unknown): number => {
          if (value === null || value === undefined || value === "") return 0;
          const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
          return isNaN(parsed) ? 0 : parsed;
        };

        // Transform backend data to match frontend format
        const transformedTrainers = trainersArray.map((trainer: Record<string, unknown>) => {
          const first10MinRate = parsePrice(trainer.first_10_min_rate);
          const perMinuteRate = parsePrice(trainer.per_minute_rate);
          const expertise = parseListString(trainer.areas_of_expertise);
          const philosophy = parseListString(trainer.training_philosophy);
          const credentials = parseListString(trainer.credentials);

          return {
            id: trainer.id as string,
            name: `${trainer.first_name as string} ${trainer.last_name as string}`,
            title: `Professional Dog Trainer`,
            experience: `${trainer.experience_years || 0}+ years`,
            photo:
              (trainer.first_name as string).charAt(0) + (trainer.last_name as string).charAt(0),
            specializations: expertise,
            philosophy: philosophy,
            credentials: credentials,
            baseRate: first10MinRate,
            perMinuteRate: perMinuteRate.toFixed(2),
            overallRating: (trainer.average_rating as number) || 0,
            totalReviews: (trainer.total_reviews as number) || 0,
            isOnline: (trainer.isOnline as boolean) || false,
            bio: (trainer.bio as string) || "Professional dog trainer",
            reviews: [], // We'll load reviews separately if needed
          };
        });
        console.log("Transformed trainers:", transformedTrainers);
        setActiveTrainers(transformedTrainers);
        console.log("Set activeTrainers state to:", transformedTrainers);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      } else {
        console.log("No trainers found or invalid response");
        setActiveTrainers([]);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      }
    } catch (err) {
      console.error("Error loading trainers:", err);
      setError("Failed to load active trainers");
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTrainingRequests = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getActiveTrainingRequests(clientId!)) as Record<
        string,
        unknown
      >;
      console.log("Active training requests response:", response);

      if (response.Code === 200 && response.data) {
        const transformedRequests = (response.data as Record<string, unknown>[]).map(
          (request: Record<string, unknown>) => ({
            ...request,
            id: (request.id || request.request_id || "") as string,
            status: (request.status || "pending") as string,
            first_10_min_rate: parseFloat(
              String(request.first_10_min_rate || request.base_rate || 0),
            ),
            per_minute_rate: parseFloat(String(request.per_minute_rate || 0)),
            total_price:
              request.total_price !== undefined && request.total_price !== null
                ? parseFloat(String(request.total_price))
                : null,
            duration_minutes: (request.duration_minutes as number | null) || null,
            created_at: (request.created_at as string | null) || null,
            accepted_at: (request.accepted_at as string | null) || null,
            payment_status: (request.payment_status as string) || "pending",
            client_joined_at: (request.client_joined_at as string | null) || null,
            trainer_joined_at: (request.trainer_joined_at as string | null) || null,
          }),
        ) as TrainingRequest[];
        setActiveTrainingRequests(transformedRequests);
      } else {
        setActiveTrainingRequests([]);
      }
    } catch (err) {
      console.error("Error loading training requests:", err);
      setError("Failed to load training requests");
    } finally {
      setLoading(false);
    }
  };

  const loadSessionHistory = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getClientSessions(clientId!)) as Record<string, unknown>;
      if (response.Code === 200) {
        const sessions = (response.data as Record<string, unknown>[]) || [];

        // Load ratings for all sessions
        const ratingsPromises = sessions
          .filter((session: Record<string, unknown>) => session.session_request_id)
          .map(async (session: Record<string, unknown>) => {
            try {
              const ratingResponse = (await apiService.getSessionRating(
                session.session_request_id as string,
              )) as Record<string, unknown>;
              if (ratingResponse.Code === 200 && ratingResponse.data) {
                return {
                  session_request_id: session.session_request_id as string,
                  rating: (ratingResponse.data as Record<string, unknown>).rating as number,
                };
              }
            } catch (err) {
              console.log(`No rating found for session request ${session.session_request_id}`, err);
            }
            return null;
          });

        const ratingsResults = await Promise.all(ratingsPromises);
        const ratingsMap: Record<string, number> = {};
        ratingsResults.forEach((result) => {
          if (result) {
            ratingsMap[result.session_request_id] = result.rating;
          }
        });

        // Add user_rating to each session
        const sessionsWithRatings = sessions.map((session: Record<string, unknown>) => ({
          ...session,
          user_rating: session.session_request_id
            ? ratingsMap[session.session_request_id as string]
            : null,
        }));

        setSessionHistory(sessionsWithRatings as SessionHistoryItem[]);

        // Load deliverables for all sessions that have session_request_id
        const deliverablesPromises = sessions
          .filter((session: Record<string, unknown>) => session.session_request_id)
          .map(async (session: Record<string, unknown>) => {
            try {
              const deliverablesResponse = (await apiService.getDeliverables(
                session.session_request_id as string,
              )) as Record<string, unknown>;
              if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                return {
                  session_request_id: session.session_request_id as string,
                  data: deliverablesResponse.data,
                };
              }
            } catch (err) {
              console.log(
                `No deliverables found for session request ${session.session_request_id}`,
                err,
              );
            }
            return null;
          });

        const deliverablesResults = await Promise.all(deliverablesPromises);
        const deliverablesMap: Record<string, DeliverableData> = {};
        deliverablesResults.forEach((result) => {
          if (result) {
            deliverablesMap[result.session_request_id] = result.data as DeliverableData;
          }
        });
        setSessionDeliverables(deliverablesMap);
      } else {
        setSessionHistory([]);
      }
    } catch (err) {
      console.error("Error loading session history:", err);
      setError("Failed to load session history");
      setSessionHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRateSession = async (session: SessionHistoryItem, rating: number): Promise<void> => {
    if (!session.session_request_id) {
      console.error("Session request ID not found");
      setError("Session request ID is required to submit a rating");
      return;
    }

    if (!session.trainer_id) {
      console.error("Trainer ID not found in session");
      setError("Trainer ID is required to submit a rating");
      return;
    }

    try {
      console.log("Submitting rating:", {
        session_request_id: session.session_request_id,
        trainer_id: session.trainer_id,
        rating,
      });
      const response = (await apiService.submitSessionReview({
        session_request_id: session.session_request_id,
        trainer_id: session.trainer_id,
        rating: rating,
      })) as Record<string, unknown>;

      console.log("Rating submission response:", response);

      if (response.Code === 200) {
        // Update the session in state with the new rating
        setSessionHistory((prev) =>
          prev.map((s) => (s.id === session.id ? { ...s, user_rating: rating } : s)),
        );
      } else {
        setError((response.Message as string) || "Failed to submit rating");
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
      setError((err as Error).message || "Failed to submit rating");
    }
  };

  const loadDeliverablesForSession = async (sessionRequestId: string): Promise<void> => {
    if (!sessionRequestId || sessionDeliverables[sessionRequestId]) {
      return; // Already loaded or no request ID
    }

    try {
      const deliverablesResponse = (await apiService.getDeliverables(sessionRequestId)) as Record<
        string,
        unknown
      >;
      if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
        setSessionDeliverables((prev) => ({
          ...prev,
          [sessionRequestId]: deliverablesResponse.data as DeliverableData,
        }));
      }
    } catch (err) {
      console.log(`No deliverables found for session request ${sessionRequestId}`, err);
    }
  };

  const handlePetUpdate = async (
    petId: string,
    updatedData: Record<string, unknown>,
  ): Promise<void> => {
    try {
      setLoading(true);
      console.log("Updating pet with data:", updatedData);

      // Map the local field names to API field names
      // The backend expects: age, race, pet_name, target_behaviors
      const apiData = {
        pet_name: updatedData.pet_name,
        age: parseInt(String(updatedData.age)),
        race: updatedData.race,
        target_behaviors: updatedData.target_behaviors || "",
      };

      console.log("API payload:", apiData);
      const response = (await apiService.updatePet(petId, apiData)) as Record<string, unknown>;
      console.log("Update response:", response);

      if (response.Code === 200) {
        // Reload pets after update
        await loadPets();
      } else {
        console.error("Update failed with response:", response);
        setError((response.Message as string) || "Failed to update pet");
      }
    } catch (err) {
      console.error("Error updating pet:", err);
      setError((err as Error).message || "Failed to update pet");
    } finally {
      setLoading(false);
    }
  };

  const handlePetDelete = async (petId: string): Promise<void> => {
    try {
      setLoading(true);
      await apiService.deletePet(petId);
      // Reload pets after deletion
      await loadPets();
    } catch (err) {
      console.error("Error deleting pet:", err);
      setError("Failed to delete pet");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPet = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Prepare data in the format expected by the API (matching PetSetup.js)
      const petData = {
        client_id: clientId,
        pet_name: newPetData.pet_name,
        pet_age: parseInt(newPetData.age),
        pet_breed: newPetData.race,
        target_behaviors: newPetData.target_behaviors,
      };

      console.log("Adding pet with data:", petData);
      const response = (await apiService.createPet(petData)) as Record<string, unknown>;
      console.log("Create pet response:", response);

      if (response.Code === 200 || response.status === "success") {
        // Reset form and close modal
        setNewPetData({
          pet_name: "",
          age: "",
          race: "",
          target_behaviors: "",
        });
        setShowAddPetModal(false);
        // Reload pets
        await loadPets();
      } else {
        setError((response.Message as string) || "Failed to add pet. Please try again.");
      }
    } catch (err) {
      console.error("Error adding pet:", err);
      setError((err as Error).message || "Failed to add pet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async (trainer: TransformedTrainer): Promise<void> => {
    console.log("=== HANDLE START TRAINING ===");
    console.log("Current pets length:", pets.length);
    console.log("Current pets:", pets);

    setSelectedTrainer(trainer);
    setSelectedPetId(""); // Reset pet selection
    setRequestExplanation(""); // Reset explanation
    setRequestDuration(30);

    // Ensure pets are loaded before showing popup
    if (pets.length === 0) {
      console.log("No pets loaded, loading pets...");
      await loadPets();
      console.log("Pets loaded, new length:", pets.length);
    }

    setShowConfirmPopup(true);
  };

  const handleConfirmTraining = async (): Promise<void> => {
    if (!selectedTrainer || !selectedPetId || !requestExplanation.trim()) {
      setError("Please select a pet and provide an explanation for the training request.");
      return;
    }

    try {
      setLoading(true);
      console.log("=== CREATING SESSION REQUEST ===");
      console.log("Client ID:", clientId);
      console.log("Trainer ID:", selectedTrainer.id);
      console.log("Pet ID:", selectedPetId);
      console.log("Explanation:", requestExplanation);
      console.log("Selected Trainer:", selectedTrainer);

      const response = (await apiService.createSessionRequest({
        client_id: clientId,
        trainer_id: selectedTrainer.id,
        pet_id: selectedPetId,
        explanation: requestExplanation,
        duration_minutes: requestDuration,
      })) as Record<string, unknown>;
      console.log("Session request created:", response);

      if (response.Code === 200) {
        // Reload active training requests to show the new request
        await loadActiveTrainingRequests();
        // Switch to requests tab to show the new request
        setActiveTab("requests");
        // Close popup
        setShowConfirmPopup(false);
        setSelectedTrainer(null);
        setSelectedPetId("");
        setRequestExplanation("");
        setRequestDuration(30);
        setError(null);
      } else {
        setError(`Error creating session request: ${response.Message as string}`);
      }
    } catch (err) {
      console.error("Error creating session request:", err);
      setError("Failed to create session request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTraining = (): void => {
    setShowConfirmPopup(false);
    setSelectedTrainer(null);
    setRequestDuration(30);
  };

  const handleCancelRequest = (request: TrainingRequest): void => {
    setSelectedRequest(request);
    setShowCancelPopup(true);
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (!selectedRequest) return;

    try {
      setLoading(true);
      const response = (await apiService.cancelSessionRequest(selectedRequest.id)) as Record<
        string,
        unknown
      >;
      console.log("Session request cancelled:", response);

      if (response.Code === 200) {
        // Reload active training requests to remove the cancelled request
        await loadActiveTrainingRequests();
        // Close popup
        setShowCancelPopup(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error("Error cancelling session request:", err);
      setError("Failed to cancel session request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCancel = (): void => {
    setShowCancelPopup(false);
    setSelectedRequest(null);
  };

  const handlePayForRequest = async (
    request: TrainingRequest,
    coupon: string | null = null,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const checkoutData: Record<string, string> = { request_id: request.id };
      if (coupon && coupon.trim()) {
        checkoutData.coupon_code = coupon.trim();
      }
      const checkoutResponse = (await apiService.createCheckoutSession(checkoutData)) as Record<
        string,
        unknown
      >;

      if (checkoutResponse.Code === 200) {
        if (checkoutResponse.mock) {
          await apiService.verifyCheckoutSession({
            session_id: checkoutResponse.session_id,
            request_id: request.id,
            amount: checkoutResponse.amount,
          });
          await loadActiveTrainingRequests();
          alert("Payment successful! You can join the session once the trainer connects.");
          setShowCouponModal(false);
          setCouponCode("");
        } else if (checkoutResponse.url) {
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.url as string;
        } else {
          console.error("No URL in checkout response:", checkoutResponse);
          setError("Payment URL not received. Please try again.");
        }
      } else {
        setError(
          (checkoutResponse.Message as string) || "Failed to initiate payment. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error initiating payment:", err);
      setError((err as Error).message || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCouponModal = (request: TrainingRequest): void => {
    setSelectedRequestForPayment(request);
    setCouponCode("");
    setShowCouponModal(true);
  };

  const handleApplyCouponAndPay = (): void => {
    if (selectedRequestForPayment) {
      handlePayForRequest(selectedRequestForPayment, couponCode);
    }
  };

  const handleJoinSession = async (requestId: string): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.registerSessionJoin(requestId, "client")) as Record<
        string,
        unknown
      >;
      console.log("Registered session join:", response);

      if (response.Code === 200) {
        window.location.href = `/video-call?request_id=${requestId}`;
      } else {
        setError((response.Message as string) || "Failed to join session");
      }
    } catch (err) {
      console.error("Error joining session:", err);
      setError("Failed to join session");
    } finally {
      setLoading(false);
    }
  };

  // Star rendering function for ratings
  const renderStars = (rating: number, size: string = "normal"): React.ReactNode[] => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    const sizeClass = size === "small" ? "text-[0.9rem]" : "text-[1.2rem]";

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} className={sizeClass}>
          ⭐
        </span>,
      );
    }

    if (hasHalfStar) {
      stars.push(
        <span key="half" className={sizeClass}>
          ✨
        </span>,
      );
    }

    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <span key={`empty-${i}`} className={sizeClass}>
          ☆
        </span>,
      );
    }

    return stars;
  };

  const handleTabChange = async (tabName: string): Promise<void> => {
    setActiveTab(tabName);
    setError(null);

    // Load data for the selected tab
    switch (tabName) {
      case "find-trainer":
        console.log("=== SWITCHING TO FIND TRAINER TAB ===");
        console.log("Current activeTrainers state:", activeTrainers);
        console.log("Forcing refresh of active trainers...");
        await loadActiveTrainers();
        break;
      case "pets":
        await loadPets();
        break;
      case "profile":
        await loadProfile();
        break;
      case "trainers":
        console.log("=== SWITCHING TO TRAINERS TAB ===");
        console.log("Current activeTrainers state:", activeTrainers);
        console.log("Forcing refresh of active trainers...");
        await loadActiveTrainers();
        break;
      case "requests":
        await loadActiveTrainingRequests();
        break;
      case "sessions":
        await loadSessionHistory();
        break;
      case "tasks":
        await loadSessionHistory(); // Load sessions to get training tasks
        break;
      default:
        break;
    }
  };

  const renderPetsTab = (): React.ReactNode => (
    <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
          My Pets
        </h2>
        <Button onClick={() => setShowAddPetModal(true)} radius="md">
          + Add Pet
        </Button>
      </div>
      {loading ? (
        <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
          Loading pets...
        </div>
      ) : error ? (
        <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
          {error}
        </div>
      ) : pets.length === 0 ? (
        <div className="text-center py-[60px] px-5 text-muted-foreground">
          <div className="text-6xl mb-5">No Pets</div>
          <p>No pets registered yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2">
          {pets.map((pet, index) => (
            <PetCard
              key={pet.id || index}
              pet={pet}
              onUpdate={handlePetUpdate}
              onDelete={handlePetDelete}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderProfileTab = (): React.ReactNode => {
    // Profile editing state is now managed at component level

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = e.target;
      setProfileEditData((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (name === "password" || name === "confirmPassword") {
        setPasswordError(null);
      }
    };

    const handleSave = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        setPasswordError(null);

        // Validate password if provided
        if (profileEditData.password) {
          if (profileEditData.password !== profileEditData.confirmPassword) {
            setPasswordError("Passwords do not match");
            return;
          }
        }

        const response = (await apiService.updateClientProfile(
          clientId!,
          profileEditData as unknown as Record<string, unknown>,
        )) as Record<string, unknown>;
        if (response.Code === 200) {
          await loadProfile();
          setIsEditingProfile(false);
          setProfileEditData({
            first_name: "",
            last_name: "",
            phone_number: "",
            address: "",
            password: "",
            confirmPassword: "",
          });
        } else {
          setError((response.Message as string) || "Failed to update profile");
        }
      } catch (err) {
        console.error("Error updating profile:", err);
        setError("Failed to update profile");
      } finally {
        setLoading(false);
      }
    };

    const handleCancel = () => {
      setIsEditingProfile(false);
      setProfileEditData({
        first_name: "",
        last_name: "",
        phone_number: "",
        address: "",
        password: "",
        confirmPassword: "",
      });
      setPasswordError(null);
    };

    return (
      <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
            My Profile
          </h2>
          {!isEditingProfile && (
            <Button onClick={() => setIsEditingProfile(true)} radius="md">
              Edit Profile
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
            Loading profile...
          </div>
        ) : error ? (
          <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
            {error}
          </div>
        ) : !profile ? (
          <div className="text-center py-[60px] px-5 text-muted-foreground">
            <div className="text-6xl mb-5">Profile</div>
            <p>Profile information not available</p>
          </div>
        ) : isEditingProfile ? (
          <div className="flex flex-col gap-4">
            {/* Profile Photo Upload Section */}
            <div className="text-center mb-[30px]">
              {profile.profile_photo_url ? (
                <img
                  src={profile.profile_photo_url}
                  alt="Profile"
                  className="w-[120px] h-[120px] rounded-full object-cover border-[3px] border-primary mb-[15px] mx-auto"
                />
              ) : (
                <div className="w-[120px] h-[120px] rounded-full bg-muted flex items-center justify-center text-[2.5rem] text-muted-foreground mx-auto mb-[15px] border-[3px] border-border">
                  👤
                </div>
              )}
              <div>
                <label
                  className={`inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md text-[0.9rem] font-medium ${uploadingPhoto ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                >
                  {uploadingPhoto ? "Uploading..." : "Change Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleClientPhotoUpload}
                    disabled={uploadingPhoto}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-[5px]">
              <label className="text-[0.9rem] font-semibold text-foreground">First Name</label>
              <TextInput
                name="first_name"
                value={profileEditData.first_name || profile.first_name}
                onChange={handleEditChange}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <label className="text-[0.9rem] font-semibold text-foreground">Last Name</label>
              <TextInput
                name="last_name"
                value={profileEditData.last_name || profile.last_name}
                onChange={handleEditChange}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <label className="text-[0.9rem] font-semibold text-foreground">Phone Number</label>
              <TextInput
                name="phone_number"
                value={profileEditData.phone_number || profile.phone_number}
                onChange={handleEditChange}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <label className="text-[0.9rem] font-semibold text-foreground">Address</label>
              <TextInput
                name="address"
                value={profileEditData.address || profile.address}
                onChange={handleEditChange}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <label className="text-[0.9rem] font-semibold text-foreground">
                New Password (optional)
              </label>
              <TextInput
                type="password"
                name="password"
                value={profileEditData.password}
                onChange={handleEditChange}
                placeholder="Leave blank to keep current password"
              />
            </div>
            {profileEditData.password && (
              <div className="flex flex-col gap-[5px]">
                <label className="text-[0.9rem] font-semibold text-foreground">
                  Confirm New Password
                </label>
                <TextInput
                  type="password"
                  name="confirmPassword"
                  value={profileEditData.confirmPassword}
                  onChange={handleEditChange}
                />
              </div>
            )}
            {passwordError && (
              <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
                {passwordError}
              </div>
            )}
            <div className="flex gap-2.5 mt-5 justify-end">
              <Button onClick={handleSave} color="green">
                Save Changes
              </Button>
              <Button onClick={handleCancel} color="gray">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-muted rounded-xl p-4 sm:p-[30px] flex flex-col sm:flex-row items-center gap-5 border-2 border-border">
            {/* Profile Photo Section */}
            <div className="text-center mb-[30px]">
              {profile.profile_photo_url ? (
                <img
                  src={profile.profile_photo_url}
                  alt="Profile"
                  className="w-[150px] h-[150px] rounded-full object-cover border-4 border-primary mb-[15px]"
                />
              ) : (
                <div className="w-[150px] h-[150px] rounded-full bg-muted flex items-center justify-center text-5xl text-muted-foreground mx-auto mb-[15px] border-4 border-border">
                  👤
                </div>
              )}
              <div>
                <label
                  className={`inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md text-[0.9rem] font-medium ${uploadingPhoto ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                >
                  {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleClientPhotoUpload}
                    disabled={uploadingPhoto}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            <div className="flex-1">
              <h3 className="text-2xl font-semibold text-foreground mb-[15px]">
                {profile.first_name} {profile.last_name}
              </h3>
              <p className="text-base text-muted-foreground mb-2">
                <strong>Email:</strong> {profile.email}
              </p>
              <p className="text-base text-muted-foreground mb-2">
                <strong>Phone:</strong> {profile.phone_number || "Not provided"}
              </p>
              <p className="text-base text-muted-foreground mb-2">
                <strong>Address:</strong> {profile.address || "Not provided"}
              </p>
              {profile.favorite_trainer && (
                <p className="text-base text-muted-foreground mb-2">
                  <strong>Favorite Trainer:</strong> {profile.favorite_trainer.name}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTrainersTab = (): React.ReactNode => {
    console.log("renderTrainersTab called - activeTrainers state:", activeTrainers);
    console.log("activeTrainers.length:", activeTrainers.length);
    console.log("loading:", loading);
    console.log("error:", error);

    const philosophyOptions = [
      "Force Free / Pure Positive",
      "Positive Reinforcement Based (+R)",
      "Clicker / Marker Training",
      "Fear Free",
      "Science Based",
      "Relationship Based",
      "Balanced",
      "LIMA (Least Intrusive, Minimally Aversive)",
      "Traditional Training Methods",
      "Alpha / Pack Based",
      "Remote Training",
    ];

    const expertiseOptions = [
      "Puppy",
      "Manners",
      "Behavior Modification",
      "Aggression Management – Dog to dog",
      "Aggression Management - human directed",
      "Reactivity",
      "Separation Anxiety",
      "Anxiety / Fears / Phobias",
      "Leash Walking",
      "Potty Training",
      "Rescue Rehab",
      "Service Dog",
      "Therapy Dog",
      "Protection",
      "Tracking",
      "Competition Obedience",
      "Scentwork",
      "Agility",
      "Conformation",
      "Herding",
      "Earthdog",
    ];

    return (
      <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
            Active Trainers
          </h2>
          <Button
            onClick={loadActiveTrainers}
            disabled={loading}
            className={`${
              refreshSuccess
                ? "bg-success text-white"
                : loading
                  ? "bg-border cursor-wait"
                  : "bg-muted border-border text-muted-foreground"
            }`}
            variant="default"
          >
            {loading ? "Refreshing..." : refreshSuccess ? "Refreshed!" : "Refresh"}
          </Button>
        </div>

        {/* Filters Section */}
        {!loading && activeTrainers.length > 0 && (
          <div className="bg-muted rounded-lg px-3 py-2 mb-4 border border-border">
            <div className="flex flex-wrap gap-2 items-start">
              {/* Search */}
              <input
                type="text"
                value={trainerFilters.search}
                onChange={(e) =>
                  setTrainerFilters({
                    ...trainerFilters,
                    search: e.target.value,
                  })
                }
                className="py-2 px-2.5 rounded-md border border-border bg-input text-foreground text-[0.85rem] min-w-[140px] flex-[0_1_200px]"
                placeholder="Search..."
              />

              {/* Training Philosophy - Custom Dropdown */}
              <div className="relative flex-[0_1_220px] min-w-[180px]" data-dropdown>
                <button
                  onClick={() => toggleDropdown("philosophy")}
                  className="py-2 px-3 rounded-md border border-border bg-card text-foreground text-[0.85rem] w-full cursor-pointer text-left flex justify-between items-center box-border"
                >
                  <span>
                    Training Philosophy{" "}
                    {trainerFilters.philosophy.length > 0
                      ? `(${trainerFilters.philosophy.length})`
                      : ""}
                  </span>
                  <span>{openDropdowns.philosophy ? "▲" : "▼"}</span>
                </button>
                {openDropdowns.philosophy && (
                  <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-md mt-1 max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
                    {philosophyOptions.map((phil) => (
                      <label
                        key={phil}
                        className="flex items-center py-2 px-3 cursor-pointer border-b border-border/50 text-foreground text-[0.85rem] bg-card hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.philosophy.includes(phil)}
                          onChange={() => handleFilterToggle("philosophy", phil)}
                          className="mr-2 cursor-pointer"
                        />
                        <span>{phil}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Area of Expertise - Custom Dropdown */}
              <div className="relative flex-[0_1_220px] min-w-[180px]" data-dropdown>
                <button
                  onClick={() => toggleDropdown("expertise")}
                  className="py-2 px-3 rounded-md border border-border bg-card text-foreground text-[0.85rem] w-full cursor-pointer text-left flex justify-between items-center box-border"
                >
                  <span>
                    Area of Expertise{" "}
                    {trainerFilters.expertise.length > 0
                      ? `(${trainerFilters.expertise.length})`
                      : ""}
                  </span>
                  <span>{openDropdowns.expertise ? "▲" : "▼"}</span>
                </button>
                {openDropdowns.expertise && (
                  <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-md mt-1 max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
                    {expertiseOptions.map((exp) => (
                      <label
                        key={exp}
                        className="flex items-center py-2 px-3 cursor-pointer border-b border-border/50 text-foreground text-[0.85rem] bg-card hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.expertise.includes(exp)}
                          onChange={() => handleFilterToggle("expertise", exp)}
                          className="mr-2 cursor-pointer"
                        />
                        <span>{exp}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Credentials - Custom Dropdown */}
              <div className="relative flex-[0_1_180px] min-w-[150px]" data-dropdown>
                <button
                  onClick={() => toggleDropdown("credentials")}
                  className="py-2 px-3 rounded-md border border-border bg-card text-foreground text-[0.85rem] w-full cursor-pointer text-left flex justify-between items-center box-border"
                >
                  <span>
                    Credentials{" "}
                    {trainerFilters.credentials.length > 0
                      ? `(${trainerFilters.credentials.length})`
                      : ""}
                  </span>
                  <span>{openDropdowns.credentials ? "▲" : "▼"}</span>
                </button>
                {openDropdowns.credentials && (
                  <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-md mt-1 max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
                    {[
                      "CDT",
                      "CPDT",
                      "CPDT-KA",
                      "KPA-CTP",
                      "CBCC-KA",
                      "CCPDT",
                      "IAABC",
                      "VSPDT",
                      "ABC",
                      "Other",
                    ].map((cred) => (
                      <label
                        key={cred}
                        className="flex items-center py-2 px-3 cursor-pointer border-b border-border/50 text-foreground text-[0.85rem] bg-card hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.credentials.includes(cred)}
                          onChange={() => handleFilterToggle("credentials", cred)}
                          className="mr-2 cursor-pointer"
                        />
                        <span>{cred}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {(trainerFilters.search ||
                trainerFilters.philosophy.length > 0 ||
                trainerFilters.expertise.length > 0 ||
                trainerFilters.credentials.length > 0) && (
                <button
                  onClick={() =>
                    setTrainerFilters({
                      search: "",
                      philosophy: [],
                      expertise: [],
                      credentials: [],
                    })
                  }
                  className="py-2 px-3 rounded-md border border-border bg-card text-muted-foreground text-[0.85rem] cursor-pointer hover:bg-muted"
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
            Loading trainers...
          </div>
        ) : error ? (
          <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
            {error}
          </div>
        ) : activeTrainers.length === 0 ? (
          <div className="text-center py-[60px] px-5 text-muted-foreground">
            <span>No active trainers available</span>
          </div>
        ) : filteredTrainers.length === 0 ? (
          <div className="text-center py-[60px] px-5 text-muted-foreground">
            <div className="text-6xl mb-5">🔍</div>
            <p>No trainers found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2">
            {filteredTrainers.map((trainer) => (
              <div
                key={trainer.id}
                className="bg-card rounded-xl p-5 border border-border shadow-sm transition-all duration-200 relative hover:border-primary/30"
              >
                {/* Trainer Header */}
                <div className="flex items-center mb-4 gap-3">
                  <div className="w-[60px] h-[60px] rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold shrink-0">
                    {trainer.photo}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-bold text-foreground mb-1">{trainer.name}</div>
                    <div className="text-[0.85rem] text-muted-foreground">
                      {trainer.experience} experience
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 text-[0.8rem] font-semibold py-1 px-2 rounded-xl shrink-0 ${trainer.isOnline ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${trainer.isOnline ? "bg-success" : "bg-destructive"}`}
                    ></div>
                    {trainer.isOnline ? "Online" : "Offline"}
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  {renderStars(trainer.overallRating)}
                  <span className="text-base font-semibold text-foreground">
                    {trainer.overallRating}
                  </span>
                  <span className="text-[0.85rem] text-muted-foreground">
                    ({trainer.totalReviews} reviews)
                  </span>
                </div>

                {/* Pricing */}
                <div className="bg-primary/10 rounded-lg p-3 mb-4">
                  <div className="text-[0.9rem] font-semibold text-foreground mb-1">
                    ⚡ Live Session Pricing
                  </div>
                  <div className="text-[0.85rem] text-muted-foreground leading-relaxed">
                    <strong>${trainer.baseRate}</strong> first 10 minutes +{" "}
                    <strong>${trainer.perMinuteRate}/min</strong> after
                    <br />
                    <em>Instant connection - start training immediately!</em>
                  </div>
                </div>

                {/* Bio */}
                <div className="text-[0.9rem] text-foreground leading-relaxed mb-4">
                  {trainer.bio}
                </div>

                {/* Specializations */}
                {trainer.specializations && trainer.specializations.length > 0 && (
                  <div className="mb-4">
                    <div className="text-[0.85rem] font-semibold text-foreground mb-2">
                      Specializations:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {trainer.specializations.map((spec, index) => (
                        <span
                          key={index}
                          className="bg-primary/10 text-primary py-1.5 px-3 rounded-[20px] text-xs border border-primary/20 font-medium"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Training Philosophy */}
                {trainer.philosophy && trainer.philosophy.length > 0 && (
                  <div className="mb-4">
                    <div className="text-[0.85rem] font-semibold text-foreground mb-2">
                      Training Philosophy:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {trainer.philosophy.map((phil, index) => (
                        <span
                          key={index}
                          className="bg-primary/10 text-primary py-1.5 px-3 rounded-[20px] text-xs border border-primary/20 font-medium"
                        >
                          {phil}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-5 pt-4 border-t border-border">
                  <Button
                    fullWidth
                    radius="md"
                    className={
                      trainer.isOnline ? "" : "!bg-muted !text-muted-foreground !cursor-not-allowed"
                    }
                    disabled={!trainer.isOnline}
                    onClick={() => trainer.isOnline && handleStartTraining(trainer)}
                  >
                    {trainer.isOnline ? "Start Training Now" : "Currently Offline"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderRequestsTab = (): React.ReactNode => (
    <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
          Active Training Requests
        </h2>
        <Button
          onClick={loadActiveTrainingRequests}
          disabled={loading}
          className={`${
            refreshSuccess
              ? "bg-success text-white"
              : loading
                ? "bg-border cursor-wait"
                : "bg-muted border-border text-muted-foreground"
          }`}
          variant="default"
        >
          {loading ? "Refreshing..." : refreshSuccess ? "Refreshed!" : "Refresh"}
        </Button>
      </div>
      {loading ? (
        <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
          Loading training requests...
        </div>
      ) : error ? (
        <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
          {error}
        </div>
      ) : activeTrainingRequests.length === 0 ? (
        <div className="text-center py-[60px] px-5 text-muted-foreground">
          <p>No active training requests</p>
          <p>Go to "Active Trainers" to start a training session!</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto px-1">
          {activeTrainingRequests.map((request) => (
            <div
              key={request.id}
              className="bg-card rounded-xl p-5 border border-border shadow-sm transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[1.1rem] sm:text-[1.2rem] font-semibold text-foreground mb-1">
                    {request.trainer_name}
                  </div>
                  <div className="text-[0.9rem] text-muted-foreground">
                    {(() => {
                      const statusLabels: Record<string, string> = {
                        pending: "Waiting for Trainer",
                        awaiting_payment: "Awaiting Your Payment",
                        accepted: "Ready to Join",
                        active: "In Progress",
                        completed: "Completed",
                      };
                      const statusClasses: Record<string, string> = {
                        pending: "text-warning",
                        awaiting_payment: "text-warning",
                        accepted: "text-info",
                        active: "text-success",
                        completed: "text-success",
                        cancelled: "text-destructive",
                      };
                      const label = statusLabels[request.status] || request.status;
                      const statusClass = statusClasses[request.status] || "text-muted-foreground";
                      return (
                        <>
                          Status: <span className={`font-semibold ${statusClass}`}>{label}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="text-[0.85rem] text-muted-foreground text-right">
                  {request.created_at ? new Date(request.created_at).toLocaleString() : ""}
                </div>
              </div>

              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="text-[0.9rem] text-foreground mb-1.5">
                  <strong>Session ID:</strong> {request.id}
                </div>
                {request.duration_minutes && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Duration:</strong> {request.duration_minutes} minutes
                  </div>
                )}
                <div className="text-[0.9rem] text-foreground mb-1.5">
                  <strong>Trainer Rate:</strong> $
                  {request.first_10_min_rate?.toFixed
                    ? request.first_10_min_rate.toFixed(2)
                    : Number(request.first_10_min_rate || 0).toFixed(2)}{" "}
                  first 10 min + ${Number(request.per_minute_rate || 0).toFixed(2)}/min after
                </div>
                {request.total_price !== null && request.total_price !== undefined && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Estimated Session Cost:</strong> {formatCurrency(request.total_price)}
                  </div>
                )}
                {request.notes && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Notes:</strong> {request.notes}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3 justify-end">
                {(request.status === "awaiting_payment" ||
                  (request.status === "pending" && request.payment_status !== "completed")) &&
                  request.payment_status !== "completed" && (
                    <Button
                      color="green"
                      radius="md"
                      onClick={() => handleOpenCouponModal(request)}
                      disabled={loading || request.payment_status === "completed"}
                    >
                      Pay {formatCurrency(request.total_price || 0)}
                    </Button>
                  )}
                {request.payment_status === "completed" && (
                  <Button variant="default" className="opacity-70 cursor-not-allowed" disabled>
                    Payment Complete
                  </Button>
                )}
                {request.payment_status === "completed" ? (
                  <Button
                    radius="md"
                    onClick={() => handleJoinSession(request.id)}
                    disabled={loading}
                  >
                    Join Session Now
                  </Button>
                ) : request.status === "accepted" || request.status === "active" ? (
                  <Button
                    radius="md"
                    onClick={() => handleJoinSession(request.id)}
                    disabled={loading}
                  >
                    Join Session
                  </Button>
                ) : request.status === "pending" && request.payment_status !== "completed" ? (
                  <Button color="yellow" className="opacity-90 cursor-not-allowed" disabled>
                    Waiting for Trainer to Accept
                  </Button>
                ) : request.status !== "awaiting_payment" && request.status !== "pending" ? (
                  <Button variant="default" className="cursor-not-allowed" disabled>
                    Unavailable
                  </Button>
                ) : null}
                <Button
                  color="red"
                  radius="md"
                  onClick={() => handleCancelRequest(request)}
                  disabled={loading}
                >
                  Cancel Request
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleViewSessionDetails = async (session: SessionHistoryItem): Promise<void> => {
    // Load deliverables if not already loaded
    if (session.session_request_id) {
      await loadDeliverablesForSession(session.session_request_id);
    }
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const renderSessionsTab = (): React.ReactNode => {
    return (
      <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
            Session History
          </h2>
          <Button
            onClick={loadSessionHistory}
            disabled={loading}
            radius="md"
            className={loading ? "cursor-not-allowed opacity-60" : ""}
          >
            Refresh
          </Button>
        </div>
        {loading ? (
          <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
            Loading sessions...
          </div>
        ) : error ? (
          <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
            {error}
          </div>
        ) : sessionHistory.length === 0 ? (
          <div className="text-center py-[60px] px-5 text-muted-foreground">
            <div className="text-6xl mb-5">No Sessions</div>
            <p>No sessions completed yet</p>
            <p className="text-[0.9rem] mt-2.5 text-muted-foreground">
              Book a session with an active trainer to get started!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {sessionHistory.map((session) => {
              const sessionDate = session.date ? new Date(session.date) : null;
              const sessionEndDate = session.end_date ? new Date(session.end_date) : null;
              const formattedDate = sessionDate ? sessionDate.toLocaleDateString() : "N/A";
              const formattedTime = sessionDate ? sessionDate.toLocaleTimeString() : "N/A";
              const formattedEndTime = sessionEndDate ? sessionEndDate.toLocaleTimeString() : null;

              return (
                <div
                  key={session.id}
                  className="bg-muted rounded-lg px-3 sm:px-5 py-4 border border-border mb-3 transition-all duration-300 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 w-full">
                    <div className="min-w-[150px]">
                      <div className="text-base font-semibold text-foreground mb-1">
                        Session #{session.id.slice(-8)}
                      </div>
                      <div className="text-[0.85rem] text-muted-foreground">{formattedDate}</div>
                    </div>
                    <div className="flex-1 flex items-center flex-wrap text-[0.9rem]">
                      <span className="text-[0.9rem] text-muted-foreground font-medium">
                        Trainer:{" "}
                      </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        {session.trainer_name}
                      </span>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium">
                        Time:{" "}
                      </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        {formattedTime}
                        {formattedEndTime ? ` – ${formattedEndTime}` : ""}
                      </span>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium">
                        Duration:{" "}
                      </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        {session.duration_minutes || session.session_duration_minutes || 0} minutes
                      </span>
                      <span className="text-muted-foreground mx-2"> | </span>
                      <span className="text-[0.9rem] text-muted-foreground font-medium">
                        Price:{" "}
                      </span>
                      <span className="text-[0.9rem] text-foreground font-medium">
                        {formatCurrency(session.price)}
                      </span>
                      {session.status && (
                        <>
                          <span className="text-muted-foreground mx-2"> | </span>
                          <span className="text-[0.9rem] text-muted-foreground font-medium">
                            Status:{" "}
                          </span>
                          <span
                            className={`text-[0.9rem] font-medium ${getStatusColor(session.status)}`}
                          >
                            {session.status}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col gap-2.5 items-start sm:items-end">
                      {/* Star Rating Component */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[0.85rem] text-foreground font-medium">
                          Rate this session:{" "}
                        </span>
                        <div className="flex gap-1 items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              onClick={() => handleRateSession(session, star)}
                              className={`text-[1.2rem] cursor-pointer ${session.user_rating && star <= session.user_rating ? "text-primary" : "text-muted-foreground"}`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        {session.user_rating && (
                          <span className="text-base font-semibold text-foreground">
                            {session.user_rating}/5
                          </span>
                        )}
                      </div>
                      <span className="bg-success text-white py-1.5 px-3 rounded-md text-[0.85rem] font-medium">
                        Completed
                      </span>
                      <Button
                        size="sm"
                        radius="md"
                        onClick={() => handleViewSessionDetails(session)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Session Details Modal */}
        <Modal
          opened={showSessionModal && selectedSession !== null}
          onClose={() => setShowSessionModal(false)}
          title="Session Details"
          size="lg"
          classNames={{
            header: "bg-card border-b border-border",
            title: "text-2xl font-semibold text-foreground",
            body: "bg-card p-6",
            close: "text-muted-foreground hover:text-foreground",
            content: "bg-card border border-border",
          }}
        >
          {selectedSession && (
            <>
              <div className="flex flex-col gap-3 mb-6 pb-6 border-b border-border">
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">
                    Session ID:{" "}
                  </span>
                  <span className="text-[0.95rem] text-foreground">
                    #{selectedSession.id.slice(-8)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">Date: </span>
                  <span className="text-[0.95rem] text-foreground">
                    {selectedSession.date
                      ? new Date(selectedSession.date).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">Time: </span>
                  <span className="text-[0.95rem] text-foreground">
                    {selectedSession.date
                      ? new Date(selectedSession.date).toLocaleTimeString()
                      : "N/A"}
                    {selectedSession.end_date
                      ? ` – ${new Date(selectedSession.end_date).toLocaleTimeString()}`
                      : ""}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">
                    Trainer:{" "}
                  </span>
                  <span className="text-[0.95rem] text-foreground">
                    {selectedSession.trainer_name}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">
                    Duration:{" "}
                  </span>
                  <span className="text-[0.95rem] text-foreground">
                    {selectedSession.duration_minutes ||
                      selectedSession.session_duration_minutes ||
                      0}{" "}
                    minutes
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">
                    Price:{" "}
                  </span>
                  <span className="text-[0.95rem] text-foreground">
                    {formatCurrency(selectedSession.price)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[0.95rem] font-semibold text-muted-foreground">
                    Status:{" "}
                  </span>
                  <span
                    className={`text-[0.95rem] ${getStatusColor(selectedSession.status || "")}`}
                  >
                    {selectedSession.status}
                  </span>
                </div>
              </div>

              {/* Deliverables from trainer */}
              {selectedSession.session_request_id &&
                sessionDeliverables[selectedSession.session_request_id] && (
                  <>
                    {sessionDeliverables[selectedSession.session_request_id].summary && (
                      <div className="mb-6">
                        <h3 className="text-[1.1rem] font-semibold text-foreground mb-3">
                          Session Summary
                        </h3>
                        <p className="text-[0.95rem] text-muted-foreground leading-relaxed max-h-[200px] overflow-y-auto p-2.5 bg-muted rounded-lg border border-border">
                          {sessionDeliverables[selectedSession.session_request_id].summary}
                        </p>
                      </div>
                    )}

                    {sessionDeliverables[selectedSession.session_request_id]?.checklist_items &&
                      sessionDeliverables[selectedSession.session_request_id]!.checklist_items!
                        .length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-[1.1rem] font-semibold text-foreground mb-3">
                            Training Checklist
                          </h3>
                          <div className="flex flex-col gap-2.5 text-left">
                            {sessionDeliverables[
                              selectedSession.session_request_id
                            ]!.checklist_items!.map((item: string, itemIndex: number) => (
                              <div key={itemIndex} className="flex items-start gap-2.5 text-left">
                                <span className="text-[0.95rem] text-primary font-semibold leading-relaxed min-w-[24px]">
                                  {itemIndex + 1}.
                                </span>
                                <span className="text-[0.95rem] text-muted-foreground leading-relaxed flex-1 text-left">
                                  {item}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </>
                )}

              {/* Fallback to session summary if no deliverables */}
              {(!selectedSession.session_request_id ||
                !sessionDeliverables[selectedSession.session_request_id]) &&
                selectedSession.summary && (
                  <div className="mb-6">
                    <h3 className="text-[1.1rem] font-semibold text-foreground mb-3">
                      Session Summary
                    </h3>
                    <p className="text-[0.95rem] text-muted-foreground leading-relaxed max-h-[200px] overflow-y-auto p-2.5 bg-muted rounded-lg border border-border">
                      {selectedSession.summary}
                    </p>
                  </div>
                )}

              {/* Show message if no deliverables yet */}
              {selectedSession.status === "completed" &&
                selectedSession.session_request_id &&
                !sessionDeliverables[selectedSession.session_request_id] && (
                  <div className="mb-6">
                    <p className="text-[0.95rem] text-muted-foreground italic text-center p-5 bg-muted rounded-lg border border-dashed border-border">
                      Deliverables are pending. Your trainer will add a summary and checklist soon.
                    </p>
                  </div>
                )}

              <div className="pt-5 border-t border-border flex justify-end">
                <Button radius="md" onClick={() => setShowSessionModal(false)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </Modal>
      </div>
    );
  };

  // If no clientId and not logged in, show error message
  if (!clientId && !isLoggedIn) {
    console.log("No clientId found and not logged in - showing error");
    return (
      <div className="flex justify-center items-center h-screen flex-col bg-background text-foreground font-['Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
        <h1>Access Denied</h1>
        <p>You need to be logged in to access the client portal.</p>
        <Button onClick={onBackToHome} radius="md" size="lg" className="mt-5">
          Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div className="font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] bg-background min-h-screen flex flex-col">
      {/* Top Navigation Bar */}
      <div className="bg-card shadow-sm border-b border-border sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto flex justify-between items-center py-2.5 px-3 sm:px-5">
          <img
            src="/images/logo.png"
            alt="Pheefee Logo"
            className="w-10 h-10 object-contain rounded-lg cursor-pointer"
            onClick={() => onBackToHome && onBackToHome()}
          />
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setLogoutModalOpen(true)}
              color="red"
              variant="outline"
              radius="md"
            >
              Logout
            </Button>
            <Modal
              opened={logoutModalOpen}
              onClose={() => setLogoutModalOpen(false)}
              title="Confirm Logout"
              centered
              size="sm"
            >
              <p className="text-foreground mb-6">Are you sure you want to logout?</p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setLogoutModalOpen(false)} radius="md">
                  Cancel
                </Button>
                <Button color="red" onClick={onLogout} radius="md">
                  Logout
                </Button>
              </div>
            </Modal>
          </div>
        </div>

        {/* Horizontal Tabs */}
        <div className="max-w-[1200px] mx-auto flex px-3 sm:px-5 gap-0 overflow-x-auto bg-card">
          <button
            onClick={() => handleTabChange("find-trainer")}
            className={`py-2.5 px-5 border-none bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
              activeTab === "find-trainer"
                ? "text-primary border-b-primary font-semibold bg-primary/10"
                : "text-muted-foreground border-b-transparent"
            }`}
          >
            Find Trainer
          </button>
          <button
            onClick={() => handleTabChange("requests")}
            className={`py-2.5 px-5 border-none bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
              activeTab === "requests"
                ? "text-primary border-b-primary font-semibold bg-primary/10"
                : "text-muted-foreground border-b-transparent"
            }`}
          >
            Training Requests
          </button>
          <button
            onClick={() => handleTabChange("sessions")}
            className={`py-2.5 px-5 border-none bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
              activeTab === "sessions"
                ? "text-primary border-b-primary font-semibold bg-primary/10"
                : "text-muted-foreground border-b-transparent"
            }`}
          >
            Sessions
          </button>
          <button
            onClick={() => handleTabChange("profile")}
            className={`py-2.5 px-5 border-none bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
              activeTab === "profile"
                ? "text-primary border-b-primary font-semibold bg-primary/10"
                : "text-muted-foreground border-b-transparent"
            }`}
          >
            My Profile
          </button>
          <button
            onClick={() => handleTabChange("pets")}
            className={`py-2.5 px-5 border-none bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 rounded-t-md mr-1 ${
              activeTab === "pets"
                ? "text-primary border-b-primary font-semibold bg-primary/10"
                : "text-muted-foreground border-b-transparent"
            }`}
          >
            My Pets
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 max-w-[1200px] mx-auto w-full py-4 sm:py-[30px] px-3 sm:px-5 overflow-y-auto bg-background">
        {/* Tab Content */}
        {activeTab === "find-trainer" && renderTrainersTab()}
        {activeTab === "pets" && renderPetsTab()}
        {activeTab === "profile" && renderProfileTab()}
        {activeTab === "requests" && renderRequestsTab()}
        {activeTab === "sessions" && renderSessionsTab()}
      </div>

      {/* Confirmation Popup */}
      <Modal
        opened={showConfirmPopup}
        onClose={handleCancelTraining}
        title=""
        withCloseButton={false}
        size="md"
        classNames={{
          content: "rounded-2xl overflow-hidden p-0",
          body: "p-0",
        }}
      >
        <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
          <h3 className="m-0 text-lg sm:text-xl font-semibold">Confirm Training Request</h3>
        </div>
        <div className="p-4 sm:p-6">
          <p className="text-[1.1rem] text-foreground mb-5 text-center leading-relaxed">
            Create an active session request with <strong>{selectedTrainer?.name}</strong>?
          </p>
          <div className="bg-muted rounded-lg p-4 mb-5">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Trainer:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                {selectedTrainer?.name}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Rate:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                ${selectedTrainer?.baseRate} first 10 min + ${selectedTrainer?.perMinuteRate}/min
                after
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Experience:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                {selectedTrainer?.experience}
              </span>
            </div>
          </div>

          {/* Pet Selection */}
          <div className="mb-5">
            <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
              Select Pet for Training:
            </label>
            <select
              value={selectedPetId}
              onChange={(e) => setSelectedPetId(e.target.value)}
              className="w-full py-2.5 px-3 border-2 border-border rounded-md text-[0.9rem] bg-card cursor-pointer transition-colors duration-200 outline-none"
              required
            >
              <option value="">Choose a pet...</option>
              {pets.map((pet) => (
                <option key={pet.id} value={pet.id}>
                  {pet.pet_name || pet.name} {pet.race ? `(${pet.race})` : ""}{" "}
                  {pet.age ? `- ${pet.age} years old` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-5">
            <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
              Session Duration (minutes):
            </label>
            <select
              value={requestDuration}
              onChange={(e) => setRequestDuration(parseInt(e.target.value, 10))}
              className="w-full py-2.5 px-3 border-2 border-border rounded-md text-[0.9rem] bg-card cursor-pointer transition-colors duration-200 outline-none"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
            </select>
          </div>

          {/* Explanation */}
          <div className="mb-5">
            <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
              Training Request Explanation:
            </label>
            <Textarea
              value={requestExplanation}
              onChange={(e) => setRequestExplanation(e.target.value)}
              placeholder="Please explain what you'd like to work on with your pet during this training session..."
              rows={4}
              required
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
          <Button
            variant="default"
            className="min-w-[100px]"
            onClick={handleCancelTraining}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            radius="md"
            className="min-w-[140px]"
            onClick={handleConfirmTraining}
            disabled={loading}
          >
            {loading ? "Creating..." : "Yes, Create Request"}
          </Button>
        </div>
      </Modal>

      {/* Add Pet Modal */}
      <Modal
        opened={showAddPetModal}
        onClose={() => {
          setShowAddPetModal(false);
          setNewPetData({
            pet_name: "",
            age: "",
            race: "",
            target_behaviors: "",
          });
          setError(null);
        }}
        title=""
        withCloseButton={false}
        size="md"
        classNames={{
          content: "rounded-2xl overflow-hidden p-0",
          body: "p-0",
        }}
      >
        <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
          <h3 className="m-0 text-lg sm:text-xl font-semibold">Add New Pet</h3>
        </div>
        <form onSubmit={handleAddPet}>
          <div className="p-4 sm:p-6">
            <div className="mb-5">
              <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
                Pet Name *
              </label>
              <TextInput
                value={newPetData.pet_name}
                onChange={(e) => setNewPetData({ ...newPetData, pet_name: e.target.value })}
                required
              />
            </div>
            <div className="mb-5">
              <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
                Age (years) *
              </label>
              <TextInput
                type="number"
                value={newPetData.age}
                onChange={(e) => setNewPetData({ ...newPetData, age: e.target.value })}
                required
                min={0}
              />
            </div>
            <div className="mb-5">
              <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
                Breed *
              </label>
              <TextInput
                value={newPetData.race}
                onChange={(e) => setNewPetData({ ...newPetData, race: e.target.value })}
                required
              />
            </div>
            <div className="mb-5">
              <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
                Target Behaviors
              </label>
              <Textarea
                value={newPetData.target_behaviors}
                onChange={(e) =>
                  setNewPetData({
                    ...newPetData,
                    target_behaviors: e.target.value,
                  })
                }
                rows={3}
                placeholder="e.g., leash pulling, jumping on people, separation anxiety..."
              />
            </div>
            {error && (
              <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
                {error}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
            <Button
              type="button"
              variant="default"
              className="min-w-[100px]"
              onClick={() => {
                setShowAddPetModal(false);
                setNewPetData({
                  pet_name: "",
                  age: "",
                  race: "",
                  target_behaviors: "",
                });
                setError(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="green"
              radius="md"
              className="min-w-[140px]"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Pet"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Cancel Confirmation Popup */}
      <Modal
        opened={showCancelPopup}
        onClose={handleCancelCancel}
        title=""
        withCloseButton={false}
        size="md"
        classNames={{
          content: "rounded-2xl overflow-hidden p-0",
          body: "p-0",
        }}
      >
        <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
          <h3 className="m-0 text-lg sm:text-xl font-semibold">Cancel Training Request</h3>
        </div>
        <div className="p-4 sm:p-6">
          <p className="text-[1.1rem] text-foreground mb-5 text-center leading-relaxed">
            Are you sure you want to cancel your training request with{" "}
            <strong>{selectedRequest?.trainer_name}</strong>?
          </p>
          <div className="bg-muted rounded-lg p-4 mb-5">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Trainer:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                {selectedRequest?.trainer_name}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Status:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                {selectedRequest?.status}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.9rem] text-muted-foreground font-medium">Requested:</span>
              <span className="text-[0.9rem] text-foreground font-semibold">
                {selectedRequest?.created_at
                  ? new Date(selectedRequest.created_at).toLocaleString()
                  : "N/A"}
              </span>
            </div>
          </div>
          <div className="bg-warning/10 border border-warning/25 rounded-lg p-3 mt-4 text-[0.9rem] text-warning text-center">
            This action cannot be undone. You will need to create a new request if you want to train
            with this trainer again.
          </div>
        </div>
        <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
          <Button
            variant="default"
            className="min-w-[100px]"
            onClick={handleCancelCancel}
            disabled={loading}
          >
            No, Keep Request
          </Button>
          <Button
            color="green"
            radius="md"
            className="min-w-[140px]"
            onClick={handleConfirmCancel}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Yes, Cancel Request"}
          </Button>
        </div>
      </Modal>

      {/* Coupon Code Modal */}
      <Modal
        opened={showCouponModal && selectedRequestForPayment !== null}
        onClose={() => {
          setShowCouponModal(false);
          setCouponCode("");
          setSelectedRequestForPayment(null);
        }}
        title=""
        withCloseButton={false}
        size="md"
        classNames={{
          content: "rounded-2xl overflow-hidden p-0",
          body: "p-0",
        }}
      >
        <div className="bg-primary text-primary-foreground py-4 sm:py-5 px-4 sm:px-6 text-center">
          <h3 className="m-0 text-lg sm:text-xl font-semibold">Enter Payment Details</h3>
        </div>
        <div className="p-4 sm:p-6">
          <div className="mb-5">
            <label className="block mb-2 font-semibold text-foreground text-[0.9rem]">
              Coupon Code (Optional):
            </label>
            <TextInput
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              maxLength={50}
            />
            <p className="text-[0.8rem] text-muted-foreground mt-1 italic">
              You can also enter a promo code during checkout on Stripe
            </p>
          </div>
          {selectedRequestForPayment && (
            <div className="mt-5 p-4 bg-muted rounded-lg border border-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[0.9rem] text-muted-foreground font-medium">
                  Session Cost:
                </span>
                <span className="text-[0.9rem] text-foreground font-semibold">
                  {formatCurrency(selectedRequestForPayment.total_price || 0)}
                </span>
              </div>
              {couponCode && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[0.9rem] text-muted-foreground font-medium">
                    Coupon Applied:
                  </span>
                  <span className="text-[0.9rem] text-foreground font-semibold">{couponCode}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 justify-end px-4 sm:px-6 pb-4 sm:pb-6">
          <Button
            variant="default"
            className="min-w-[100px]"
            onClick={() => {
              setShowCouponModal(false);
              setCouponCode("");
              setSelectedRequestForPayment(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            radius="md"
            className="min-w-[140px]"
            onClick={handleApplyCouponAndPay}
            disabled={loading}
          >
            {loading ? "Processing..." : "Proceed to Payment"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ClientPortal;
